import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  Container,
  ToggleButton,
  ToggleButtonGroup,
  FormGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  registerables,
} from 'chart.js';
import { Chart as ChartComponent } from 'react-chartjs-2';
import { FinancialChart } from './charts/FinancialChart';
import { FinancialData } from './charts/types/chartTypes';
import { TransactionDetailTable, Transaction } from './TransactionDetailTable';
import { useAuth } from '../contexts/AuthContext';
import { 
  useAccountBalances, 
  useFiscalMonthlyData,
  useFiscalYearlyData,
  useOrganizationSettings,
  useTransactions
} from '../hooks/useTransactions';
import { getCurrentFiscalYear, getFiscalYearStartDate, getFiscalYearEndDate } from '../utils/fiscalYear';
import { AccountData } from '../types';

// Chart.js の全コンポーネント登録
ChartJS.register(...registerables);


const Dashboard: React.FC = () => {
  const { organization } = useAuth();
  const [chartPeriod, setChartPeriod] = useState<'monthly' | 'yearly'>('yearly');
  const [showGeneral, setShowGeneral] = useState(true);
  const [showReserve, setShowReserve] = useState(true);
  
  // クリックしたグラフの明細表示用state
  const [selectedChartData, setSelectedChartData] = useState<{
    period: string;
    accountType: 'general' | 'reserve';
    type: 'income' | 'expense';
    amount: number;
  } | null>(null);
  
  // 組織設定を取得
  const { data: orgSettings, isLoading: settingsLoading } = useOrganizationSettings();
  
  // 現在の会計年度を取得（useEffectで使用）
  const [currentFiscalYear, setCurrentFiscalYear] = useState<number | null>(null);
  
  // 設定が読み込まれたら会計年度を更新
  useEffect(() => {
    if (orgSettings?.fiscalYearStartMonth && !settingsLoading) {
      const newFiscalYear = getCurrentFiscalYear(orgSettings.fiscalYearStartMonth);
      setCurrentFiscalYear(newFiscalYear);
    }
  }, [orgSettings?.fiscalYearStartMonth, settingsLoading]);

  // 実データフェッチ（会計年度ベース）
  const { data: accountBalances, isLoading: balancesLoading, error: balancesError } = useAccountBalances();
  const { data: monthlyData, isLoading: monthlyLoading, error: monthlyError } = useFiscalMonthlyData(currentFiscalYear!);
  const { data: yearlyData, isLoading: yearlyLoading, error: yearlyError } = useFiscalYearlyData(5, currentFiscalYear!);
  
  
  // 選択された期間の詳細データを取得
  const getDetailDateRange = () => {
    if (!selectedChartData || !orgSettings?.fiscalYearStartMonth) return undefined;
    
    if (chartPeriod === 'monthly' && currentFiscalYear) {
      // 月別データの場合、選択された月の範囲を算出
      const monthMatch = selectedChartData.period.match(/\d+年(\d+)月/);
      if (monthMatch) {
        const month = parseInt(monthMatch[1]);
        const year = selectedChartData.period.includes(`${currentFiscalYear}年`) ? currentFiscalYear : currentFiscalYear + 1;
        const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
        const endDate = new Date(year, month, 0).toISOString().split('T')[0]; // 月末日
        return { startDate, endDate };
      }
    } else {
      // 年別データの場合、選択された年度の範囲を算出
      const yearMatch = selectedChartData.period.match(/(\d+)年度/);
      if (yearMatch) {
        const fiscalYear = parseInt(yearMatch[1]);
        const startDate = getFiscalYearStartDate(fiscalYear, orgSettings.fiscalYearStartMonth);
        const endDate = getFiscalYearEndDate(fiscalYear, orgSettings.fiscalYearStartMonth);
        return {
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0]
        };
      }
    }
    return undefined;
  };
  
  const dateRange = getDetailDateRange();
  const { data: detailTransactions } = useTransactions(
    dateRange ? dateRange : undefined
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
    }).format(amount);
  };

  // 共通コンポーネント用のデータ変換
  const convertToFinancialData = (): FinancialData[] => {
    const isMonthly = chartPeriod === 'monthly';
    const sourceData = isMonthly ? (monthlyData || []) : (yearlyData || []);
    
    return sourceData.map(d => ({
      year: isMonthly ? currentFiscalYear || 0 : (d as any).year,
      month: isMonthly ? (d as any).month : undefined,
      generalIncome: d.generalIncome,
      generalExpense: d.generalExpense,
      reserveIncome: d.reserveIncome,
      reserveExpense: d.reserveExpense,
    }));
  };

  const getAccountType = (): 'general' | 'reserve' | 'both' => {
    if (showGeneral && showReserve) return 'both';
    if (showGeneral) return 'general';
    if (showReserve) return 'reserve';
    return 'both';
  };

  const handlePeriodChange = (
    _event: React.MouseEvent<HTMLElement>,
    newPeriod: 'monthly' | 'yearly' | null,
  ) => {
    if (newPeriod !== null) {
      setChartPeriod(newPeriod);
    }
  };

  const handlePreviousYear = () => {
    setCurrentFiscalYear(prev => prev ? prev - 1 : prev);
  };

  const handleNextYear = () => {
    setCurrentFiscalYear(prev => prev ? prev + 1 : prev);
  };
  
  
  // 明細データのフィルタリング
  const getFilteredDetailTransactions = (): Transaction[] => {
    if (!selectedChartData || !detailTransactions || !orgSettings?.fiscalYearStartMonth) return [];
    
    // 選択された期間から年月を抽出
    let targetYear: number | null = null;
    let targetMonth: number | null = null;
    
    if (chartPeriod === 'monthly' && currentFiscalYear) {
      // 月別データの場合
      const monthMatch = selectedChartData.period.match(/(\d+)月/);
      if (monthMatch) {
        targetMonth = parseInt(monthMatch[1]);
        // 会計年度を考慮して年を決定
        if (targetMonth >= orgSettings.fiscalYearStartMonth) {
          targetYear = currentFiscalYear;
        } else {
          targetYear = currentFiscalYear + 1;
        }
      }
    } else {
      // 年別データの場合
      const yearMatch = selectedChartData.period.match(/(\d+)年度/);
      if (yearMatch) {
        targetYear = parseInt(yearMatch[1]);
      }
    }
    
    return detailTransactions.filter(originalTransaction => {
      // 基本条件：会計種別と収支種別が一致
      const baseMatch = originalTransaction.account_type === selectedChartData.accountType &&
                       originalTransaction.type === selectedChartData.type;
      
      if (!baseMatch) return false;
      
      // 期間条件
      const transactionDate = new Date(originalTransaction.transaction_date);
      const transactionYear = transactionDate.getFullYear();
      const transactionMonth = transactionDate.getMonth() + 1;
      
      if (chartPeriod === 'monthly' && targetYear && targetMonth) {
        // 月別表示：該当年月のデータのみ
        return transactionYear === targetYear && transactionMonth === targetMonth;
      } else if (chartPeriod === 'yearly' && targetYear) {
        // 年別表示：該当年度のデータ（会計年度を考慮）
        const transactionFiscalYear = transactionMonth >= orgSettings.fiscalYearStartMonth 
          ? transactionYear 
          : transactionYear - 1;
        return transactionFiscalYear === targetYear;
      }
      
      // 期間が特定できない場合はフィルタリングしない
      return false;
    }).map(t => ({
      id: t.id,
      transaction_date: t.transaction_date,
      amount: t.amount,
      type: t.type,
      account_type: t.account_type,
      category: t.category,
      description: t.description,
      reference_number: t.reference_number,
    }));
  };

  // ローディング状態とエラーハンドリング
  if (balancesLoading || monthlyLoading || yearlyLoading || settingsLoading || currentFiscalYear === null) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (balancesError || monthlyError || yearlyError) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">
          データの取得中にエラーが発生しました: {balancesError?.message || monthlyError?.message || yearlyError?.message}
        </Alert>
      </Container>
    );
  }

  // チャートデータの準備 - 共通コンポーネント用
  const financialData = convertToFinancialData();

  // チャートクリックハンドラ - 新しいAPI用
  const handleFinancialChartClick = (clickEvent: any) => {
    if (!clickEvent.accountType || !clickEvent.type) return;
    
    setSelectedChartData({
      period: clickEvent.label,
      accountType: clickEvent.accountType,
      type: clickEvent.type,
      amount: clickEvent.value
    });
  };


  // 口座残高データの準備 - 最新年のデータから計算
  const calculateLatestBalance = (accountType: 'general' | 'reserve') => {
    if (!yearlyData || yearlyData.length === 0) {
      // データがない場合はaccountBalancesから取得
      const balance = accountBalances?.find(b => b.account_type === accountType);
      return balance?.balance || 0;
    }
    
    // 最新年のデータを取得
    const latestYear = yearlyData[yearlyData.length - 1];
    
    // 累積残高を計算
    let cumulativeBalance = 0;
    for (const yearData of yearlyData) {
      if (accountType === 'general') {
        cumulativeBalance += (yearData.generalIncome - yearData.generalExpense);
      } else {
        cumulativeBalance += (yearData.reserveIncome - yearData.reserveExpense);
      }
    }
    
    return cumulativeBalance;
  };

  // 口座残高データの準備
  const accountData: AccountData[] = [
    {
      id: 'general',
      name: '一般会計',
      balance: calculateLatestBalance('general'),
      type: 'general'
    },
    {
      id: 'reserve',
      name: '積立会計',
      balance: calculateLatestBalance('reserve'),
      type: 'reserve'
    }
  ];

  
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {organization?.name || 'マンション管理組合'} 収支ダッシュボード
      </Typography>
      
      {/* 口座残高カード */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        {accountData.map((account) => (
          <Box key={account.id} sx={{ flex: '1 1 300px', minWidth: '300px' }}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" component="h2">
                  {account.name}
                </Typography>
                <Typography 
                  variant="h4" 
                  color={account.balance >= 0 ? 'primary' : 'error'} 
                  sx={{ mt: 2 }}
                  fontWeight="bold"
                >
                  {formatCurrency(account.balance)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  現在残高
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* 収支グラフ */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6">
              収支推移
            </Typography>
            {/* 月単位表示時の会計年度ナビゲーション */}
            {chartPeriod === 'monthly' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <IconButton 
                  onClick={handlePreviousYear}
                  size="small"
                  disabled={!currentFiscalYear}
                >
                  <ChevronLeft />
                </IconButton>
                <Typography variant="body1" sx={{ minWidth: '120px', textAlign: 'center', fontWeight: 'bold' }}>
                  {currentFiscalYear || '---'}年度
                </Typography>
                <IconButton 
                  onClick={handleNextYear}
                  size="small"
                  disabled={!currentFiscalYear}
                >
                  <ChevronRight />
                </IconButton>
              </Box>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* 表示切り替えチェックボックス */}
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showGeneral}
                    onChange={(e) => setShowGeneral(e.target.checked)}
                    color="primary"
                  />
                }
                label="一般会計"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showReserve}
                    onChange={(e) => setShowReserve(e.target.checked)}
                    color="secondary"
                  />
                }
                label="積立会計"
              />
            </FormGroup>

            {/* 期間切り替え */}
            <ToggleButtonGroup
              value={chartPeriod}
              exclusive
              onChange={handlePeriodChange}
              size="small"
            >
              <ToggleButton value="monthly">
                月単位
              </ToggleButton>
              <ToggleButton value="yearly">
                年単位
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* グラフの説明 */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            📊 収入（正の棒）・支出（負の棒）・収支（線グラフ）で財務状況を可視化
            {showGeneral && showReserve && ' • 両方選択時は積み上げ表示'}
            {chartPeriod === 'monthly' && currentFiscalYear && orgSettings?.fiscalYearStartMonth && ` • ${currentFiscalYear}年度（${orgSettings.fiscalYearStartMonth}月〜${orgSettings.fiscalYearStartMonth === 1 ? 12 : orgSettings.fiscalYearStartMonth - 1}月）`}
            • 棒グラフをクリックすると該当する取引明細が表示されます
          </Typography>
        </Box>
        
        {(showGeneral || showReserve) ? (
          <FinancialChart
            data={financialData}
            period={chartPeriod}
            accountType={getAccountType()}
            showBalanceLine={true}
            showCumulativeBalance={true}
            onChartClick={handleFinancialChartClick}
            height={450}
            currentFiscalYear={currentFiscalYear}
          />
        ) : (
          <Box sx={{ height: 450, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              表示する会計を選択してください
            </Typography>
          </Box>
        )}
      </Paper>

      
      {/* 明細表示 */}
      {selectedChartData && (
        <TransactionDetailTable
          transactions={getFilteredDetailTransactions()}
          title={selectedChartData.period}
          subtitle={`${selectedChartData.accountType === 'general' ? '一般会計' : '積立会計'} ${selectedChartData.type === 'income' ? '収入' : '支出'}明細`}
          totalAmount={selectedChartData.amount}
        />
      )}
    </Container>
  );
};

export default Dashboard;